/**
 * Deposit Page - Generate deposit addresses and track incoming deposits
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowDownToLine,
  ClipboardCopy,
  CheckCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  QrCode
} from 'lucide-react';
import { api } from '../utils/api';
import { cn } from '../utils/cn';

// Supported chains for deposits
const SUPPORTED_CHAINS = [
  { id: 1, name: 'Ethereum', symbol: 'ETH', icon: '⟠', color: 'from-blue-500 to-purple-500', confirmations: 12 },
  { id: 42161, name: 'Arbitrum', symbol: 'ARB', icon: '🔷', color: 'from-blue-400 to-cyan-400', confirmations: 20 },
  { id: 137, name: 'Polygon', symbol: 'MATIC', icon: '🟣', color: 'from-purple-500 to-pink-500', confirmations: 128 },
  { id: 56, name: 'BSC', symbol: 'BNB', icon: '🟡', color: 'from-yellow-500 to-orange-500', confirmations: 15 },
  { id: 8453, name: 'Base', symbol: 'ETH', icon: '🔵', color: 'from-blue-500 to-indigo-500', confirmations: 20 },
  { id: 10, name: 'Optimism', symbol: 'ETH', icon: '🔴', color: 'from-red-500 to-pink-500', confirmations: 20 },
];

// Common tokens per chain
const CHAIN_TOKENS: Record<number, { symbol: string; address: string; decimals: number }[]> = {
  1: [
    { symbol: 'ETH', address: 'NATIVE', decimals: 18 },
    { symbol: 'USDT', address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6 },
    { symbol: 'USDC', address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6 },
    { symbol: 'WBTC', address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', decimals: 8 },
  ],
  42161: [
    { symbol: 'ETH', address: 'NATIVE', decimals: 18 },
    { symbol: 'USDT', address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', decimals: 6 },
    { symbol: 'USDC', address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', decimals: 6 },
  ],
  137: [
    { symbol: 'MATIC', address: 'NATIVE', decimals: 18 },
    { symbol: 'USDT', address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6 },
    { symbol: 'USDC', address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', decimals: 6 },
  ],
  56: [
    { symbol: 'BNB', address: 'NATIVE', decimals: 18 },
    { symbol: 'USDT', address: '0x55d398326f99059fF775485246999027B3197955', decimals: 18 },
    { symbol: 'USDC', address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', decimals: 18 },
  ],
  8453: [
    { symbol: 'ETH', address: 'NATIVE', decimals: 18 },
    { symbol: 'USDC', address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', decimals: 6 },
  ],
  10: [
    { symbol: 'ETH', address: 'NATIVE', decimals: 18 },
    { symbol: 'USDT', address: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58', decimals: 6 },
    { symbol: 'USDC', address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85', decimals: 6 },
  ],
};

// QR Code component using SVG generation
function QRCode({ value, size = 200 }: { value: string; size?: number }) {
  // Simple QR code placeholder - in production, use a library like qrcode.react
  const [qrUrl, setQrUrl] = useState('');

  useEffect(() => {
    // Using Google Charts API for QR code generation
    const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&bgcolor=1a1a2e&color=22d3ee`;
    setQrUrl(url);
  }, [value, size]);

  return (
    <div className="relative bg-white rounded-xl p-4 inline-block">
      {qrUrl ? (
        <img 
          src={qrUrl} 
          alt="Deposit Address QR Code" 
          width={size} 
          height={size}
          className="rounded-lg"
        />
      ) : (
        <div 
          className="flex items-center justify-center bg-dark-300 rounded-lg"
          style={{ width: size, height: size }}
        >
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  );
}

// Deposit status badge
function StatusBadge({ status, confirms, required }: { status: string; confirms?: number; required?: number }) {
  const getStatusConfig = () => {
    switch (status) {
      case 'confirmed':
        return { 
          icon: CheckCircle, 
          text: 'Confirmed', 
          className: 'bg-green-500/20 text-green-400 border-green-500/30' 
        };
      case 'confirming':
        return { 
          icon: Clock, 
          text: `${confirms}/${required} Confirmations`, 
          className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' 
        };
      case 'pending':
        return { 
          icon: Clock, 
          text: 'Pending', 
          className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' 
        };
      case 'failed':
        return { 
          icon: AlertTriangle, 
          text: 'Failed', 
          className: 'bg-red-500/20 text-red-400 border-red-500/30' 
        };
      default:
        return { 
          icon: Clock, 
          text: status, 
          className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' 
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.className}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.text}
    </span>
  );
}

export default function Deposit() {
  const queryClient = useQueryClient();
  const [selectedChain, setSelectedChain] = useState(SUPPORTED_CHAINS[0]);
  const [copied, setCopied] = useState(false);

  // Fetch deposit address for selected chain
  const { data: depositAddress, isLoading: addressLoading, refetch: refetchAddress } = useQuery({
    queryKey: ['deposit-address', selectedChain.id],
    queryFn: async () => {
      try {
        const response = await api.get(`/v2/trading/wallet/deposit-address?chainId=${selectedChain.id}`);
        return response.data;
      } catch (error) {
        // Return mock data for development
        return {
          address: '0x742d35Cc6634C0532925a3b844Bc9e7595f8fE21',
          chainId: selectedChain.id,
          chainName: selectedChain.name,
        };
      }
    },
  });

  // Fetch pending deposits
  const { data: deposits, isLoading: depositsLoading } = useQuery({
    queryKey: ['deposits'],
    queryFn: async () => {
      try {
        const response = await api.get('/deposits');
        return response.data;
      } catch (error) {
        // Mock data for development
        return [
          {
            id: '1',
            asset: 'ETH',
            amount: '0.5',
            chainId: 1,
            status: 'confirmed',
            confirms: 12,
            requiredConfs: 12,
            txHash: '0x1234...abcd',
            createdAt: new Date(Date.now() - 3600000).toISOString(),
          },
          {
            id: '2',
            asset: 'USDT',
            amount: '1000',
            chainId: 1,
            status: 'confirming',
            confirms: 8,
            requiredConfs: 12,
            txHash: '0x5678...efgh',
            createdAt: new Date(Date.now() - 1800000).toISOString(),
          },
        ];
      }
    },
    refetchInterval: 10000, // Poll every 10 seconds
  });

  // Copy address to clipboard
  const copyAddress = () => {
    if (depositAddress?.address) {
      navigator.clipboard.writeText(depositAddress.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Format time ago
  const timeAgo = (date: string) => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Deposit</h1>
          <p className="text-gray-400 mt-1">Deposit crypto assets to your HybridX account</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Deposit Address Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Chain Selection */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Select Network</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {SUPPORTED_CHAINS.map((chain) => (
                <button
                  key={chain.id}
                  onClick={() => setSelectedChain(chain)}
                  className={`p-4 rounded-xl border transition-all ${
                    selectedChain.id === chain.id
                      ? 'border-primary bg-primary/10'
                      : 'border-dark-100 hover:border-dark-50 bg-dark-200/50'
                  }`}
                >
                  <div className="text-2xl mb-2">{chain.icon}</div>
                  <div className="font-medium text-sm">{chain.name}</div>
                  <div className="text-xs text-gray-500">{chain.symbol}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Deposit Address */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {selectedChain.name} Deposit Address
              </h2>
              <button 
                onClick={() => refetchAddress()}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>

            {addressLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* QR Code */}
                <div className="flex-shrink-0">
                  <QRCode value={depositAddress?.address || ''} size={180} />
                </div>

                {/* Address and Instructions */}
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Your {selectedChain.name} Deposit Address
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-dark-200 rounded-lg px-4 py-3 font-mono text-sm break-all">
                        {depositAddress?.address}
                      </div>
                      <button
                        onClick={copyAddress}
                        className={`p-3 rounded-lg transition-all ${
                          copied 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-dark-200 hover:bg-dark-100 text-gray-400'
                        }`}
                      >
                        {copied ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <ClipboardCopy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Supported Tokens */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Supported Tokens
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {(CHAIN_TOKENS[selectedChain.id] || []).map((token) => (
                        <span 
                          key={token.symbol}
                          className="px-3 py-1 bg-dark-200 rounded-full text-sm font-medium"
                        >
                          {token.symbol}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="text-yellow-400 font-medium mb-1">Important</p>
                        <ul className="text-gray-400 space-y-1 text-xs">
                          <li>• Only send {selectedChain.name} network assets to this address</li>
                          <li>• Minimum deposit: 0.001 {selectedChain.symbol}</li>
                          <li>• {selectedChain.confirmations} confirmations required</li>
                          <li>• Sending other network tokens will result in permanent loss</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Recent Deposits */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Recent Deposits</h2>
            
            {depositsLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : deposits && deposits.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-400 text-sm border-b border-dark-100">
                      <th className="pb-3 font-medium">Asset</th>
                      <th className="pb-3 font-medium">Amount</th>
                      <th className="pb-3 font-medium">Network</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Time</th>
                      <th className="pb-3 font-medium">TX Hash</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-100">
                    {deposits.map((deposit: any) => {
                      const chain = SUPPORTED_CHAINS.find(c => c.id === deposit.chainId);
                      return (
                        <tr key={deposit.id} className="text-sm">
                          <td className="py-4">
                            <span className="font-medium">{deposit.asset}</span>
                          </td>
                          <td className="py-4">
                            {parseFloat(deposit.amount).toLocaleString(undefined, { 
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 8 
                            })}
                          </td>
                          <td className="py-4">
                            <span className="flex items-center gap-2">
                              <span>{chain?.icon}</span>
                              <span className="text-gray-400">{chain?.name}</span>
                            </span>
                          </td>
                          <td className="py-4">
                            <StatusBadge 
                              status={deposit.status} 
                              confirms={deposit.confirms}
                              required={deposit.requiredConfs}
                            />
                          </td>
                          <td className="py-4 text-gray-400">
                            {timeAgo(deposit.createdAt)}
                          </td>
                          <td className="py-4">
                            <a 
                              href={`https://etherscan.io/tx/${deposit.txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline font-mono text-xs"
                            >
                              {deposit.txHash?.slice(0, 10)}...
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <ArrowDownToLine className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No deposits yet</p>
                <p className="text-gray-500 text-sm mt-1">
                  Deposits will appear here once detected
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Quick Info */}
        <div className="space-y-6">
          {/* Network Info */}
          <div className="card p-6">
            <h3 className="font-semibold mb-4">Network Info</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-400">Network</span>
                <span className="font-medium">{selectedChain.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Chain ID</span>
                <span className="font-mono text-sm">{selectedChain.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Confirmations</span>
                <span>{selectedChain.confirmations}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Est. Time</span>
                <span>~{Math.ceil(selectedChain.confirmations * 0.2)} min</span>
              </div>
            </div>
          </div>

          {/* Deposit Stats */}
          <div className="card p-6">
            <h3 className="font-semibold mb-4">Your Deposit Stats</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-400">Total Deposits</span>
                <span className="font-medium">{deposits?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Pending</span>
                <span className="text-yellow-400">
                  {deposits?.filter((d: any) => d.status !== 'confirmed').length || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Confirmed</span>
                <span className="text-green-400">
                  {deposits?.filter((d: any) => d.status === 'confirmed').length || 0}
                </span>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="card p-6">
            <h3 className="font-semibold mb-4">FAQ</h3>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-gray-400 mb-1">How long do deposits take?</p>
                <p className="text-gray-300">
                  Deposits are credited after {selectedChain.confirmations} network confirmations, 
                  typically 2-5 minutes.
                </p>
              </div>
              <div>
                <p className="text-gray-400 mb-1">Is there a minimum deposit?</p>
                <p className="text-gray-300">
                  Yes, minimum is 0.001 {selectedChain.symbol} or $1 equivalent for tokens.
                </p>
              </div>
              <div>
                <p className="text-gray-400 mb-1">Are there deposit fees?</p>
                <p className="text-gray-300">
                  No, HybridX does not charge deposit fees. You only pay network gas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
